import { IPlatformService } from '@lwolf-common/platform-service'
import PlatformService from '@lwolf-common/platform-service/build/PlatformService'

interface LoadPlatformServiceOptions {
  lwDomain: string
  token: string
}

export interface LoadedPlatformService {
  platformService: IPlatformService
  activeClientId?: string
}

let instance: Promise<LoadedPlatformService> | undefined

const RELAUNCH_STORAGE_KEY = 'cc:platformServiceRelaunches'
const RELAUNCH_WINDOW_MS = 5 * 60 * 1000
const MAX_RELAUNCHES_IN_WINDOW = 3

function readRelaunches(): number[] {
  try {
    const timestamps: unknown = JSON.parse(
      window.sessionStorage.getItem(RELAUNCH_STORAGE_KEY) || '[]'
    )
    const cutoff = Date.now() - RELAUNCH_WINDOW_MS
    return Array.isArray(timestamps)
      ? timestamps.filter(
          (timestamp): timestamp is number =>
            typeof timestamp === 'number' && timestamp > cutoff
        )
      : []
  } catch {
    return []
  }
}

function recordRelaunch() {
  try {
    window.sessionStorage.setItem(
      RELAUNCH_STORAGE_KEY,
      JSON.stringify([...readRelaunches(), Date.now()])
    )
  } catch {
    // Storage unavailable; the loop guard simply can't engage.
  }
}

export function isRelaunchLoop() {
  return readRelaunches().length >= MAX_RELAUNCHES_IN_WINDOW
}

export function loadPlatformService({
  lwDomain,
  token
}: LoadPlatformServiceOptions): Promise<LoadedPlatformService> {
  if (instance) return instance

  if (isRelaunchLoop()) {
    return Promise.reject(
      new Error(
        'PlatformService re-launched repeatedly; skipping initialization to break the loop.'
      )
    )
  }

  instance = (async () => {
    const platformService: IPlatformService = await PlatformService.initialize({
      lwDomain,
      token,
      appId: 'cc',
      beforeSignOutCallback: recordRelaunch
    })

    return {
      platformService,
      activeClientId: await platformService?.getActiveClientId()
    }
  })()

  return instance
}
